import { useMemo, useState } from "react";

import { useMediaQuery } from "@mui/material";
import {
    AbfLoanExpanded,
    AbfOrderFundingType,
    BsMetaUtil,
    LoanFilterType,
    MAX_LOANS_PAGE_SIZE,
    UserLoanGroup,
    UserLoansFilter,
    calculateLoanHealth,
    getZcLedger,
    summarizeLedgerAccount,
    useStandardLoansGroupedByPrincipalMint,
    useUserLoanGroups,
    useUserStandardLoansPaginated,
    userLoanGroupTableKey
} from "@bridgesplit/abf-react";
import {
    MEDIA,
    MobilePortfolioCards,
    MobilePortfolioPagination,
    MobilePortfolioRowConfig,
    TABLE_CTA_COLUMN_KEY,
    TableColumn,
    TableKey,
    TableRow,
    Table,
    createRows,
    useCheckboxTableController
} from "@bridgesplit/ui";
import { ALL_VALUE, AllValueType, EMPTY_STRING, SortDirection, formatPercent, isAll } from "@bridgesplit/utils";
import { useLoansPagination } from "app/api/loans";
import { useChunkedPaginationController, useClampPaginationOnCountChange } from "app/api/shared";
import { LoanAction } from "app/components/loan/side-card/types";
import { AppDialog, AppDialogOpen, useAppDialog } from "app/utils";

import { useNavbarHeight } from "../navbar";
import { LoanHealthTag, LoanName, TableCardWrapper, getLoanAssetPath } from "../common";
import { BorrowLoanStats } from "./stats";
import { SectionCollapse, SectionSkeleton } from "./PortfolioSections";
import {
    BulkActionsButton,
    CELL_WIDTH,
    CELL_WIDTH_DEFAULTS,
    getDefaultPaginationController,
    GROUP_CTA_CELL,
    GROUP_HEADER_CELL_TOOLTIPS,
    GroupHealthCell,
    GroupNameCell,
    LoanActionsButton,
    buildMergeKeyByAddress,
    getMergeDisabledReason,
    PairTokenValueCell,
    TableCell,
    useSelectedMergeGroupCount
} from "./common";
import { HealthApyChipStats, PortfolioAllChip, PortfolioTokenChip } from "./mobileCards";

const PAGE_SIZE = 5;

const LOANS_ROW_CONFIG: MobilePortfolioRowConfig = {
    headerDataIndex: "loan",
    primaryValueDataIndex: "debt",
    statDataIndexes: ["collateral", "health", "borrowApy"]
};

function groupDebtUsd(g: UserLoanGroup): number {
    return g.aggregate.principalUsd + g.aggregate.interestAccruedUsd;
}

interface LoansSectionProps {
    isExpanded: boolean;
    isUsdValueLoading: boolean;
}

export default function LoansSection({ isExpanded, isUsdValueLoading }: LoansSectionProps) {
    const isMobile = useMediaQuery(MEDIA.SM.below);

    const pagination = useChunkedPaginationController<UserLoanGroup, UserLoansFilter>(
        {
            userAddress: undefined,
            principalMints: undefined,
            collateralMints: undefined,
            collateralIdentifiers: undefined,
            filterType: LoanFilterType.Active,
            orderFundingTypes: AbfOrderFundingType.Standard,
            sortDirection: SortDirection.Desc
        },
        PAGE_SIZE
    );
    const {
        groups,
        totalGroups,
        aggregate: desktopAggregate,
        isLoading
    } = useUserLoanGroups(pagination.backendPagination, isMobile);
    useClampPaginationOnCountChange(pagination.clampPageToCount, totalGroups);

    const visibleGroups = useMemo(() => pagination.visibleData(groups) ?? [], [groups, pagination]);
    const visibleMints = useMemo(() => visibleGroups.map((g) => g.principalMint), [visibleGroups]);

    const { loansByMint, isLoading: detailsLoading } = useStandardLoansGroupedByPrincipalMint(visibleMints, {
        skip: isMobile
    });

    const mobile = useMobileLoansView(isMobile);
    const aggregate = isMobile ? mobile.aggregate : desktopAggregate;

    const { selected, setSelected } = useCheckboxTableController();
    const selectedLoanAddresses = useMemo(
        () => Array.from(selected).filter((k): k is string => typeof k === "string"),
        [selected]
    );

    const mergeSource = isMobile ? mobile.mergeSource : loansByMint;
    const mergeKeyByAddress = useMemo(() => buildMergeKeyByAddress(mergeSource, (loan) => loan), [mergeSource]);
    const selectedMergeGroupCount = useSelectedMergeGroupCount(selectedLoanAddresses, mergeKeyByAddress);

    const tableRows = useMemo(() => createRows<UserLoanGroup>(visibleGroups, userLoanGroupTableKey), [visibleGroups]);

    const { open } = useAppDialog();
    const { height: navbarHeight } = useNavbarHeight();
    const groupedColumnDefs = useMemo(() => getGroupColumnDefs(), []);
    const itemColumnDefs = useMemo(() => getLoanDetailColumnDefs(open), [open]);

    const mergeAction = (
        <BulkLoanActionsButton
            selectedLoanAddresses={selectedLoanAddresses}
            mergeDisabledReason={getMergeDisabledReason(selectedMergeGroupCount, "loans")}
        />
    );

    if (isUsdValueLoading) return <SectionSkeleton />;

    return (
        <SectionCollapse isExpanded={isExpanded}>
            <BorrowLoanStats aggregate={aggregate} />
            <TableCardWrapper flushOnMobile>
                {isMobile ? (
                    <MobilePortfolioCards<UserLoanGroup, AbfLoanExpanded>
                        filterLabel="Filter by debt"
                        activeFilter={mobile.activeFilter}
                        onFilterChange={mobile.onFilterChange}
                        groups={mobile.chipRows}
                        renderChip={(row) => <LoanChipContent group={row?.data} />}
                        groupsLoading={mobile.allGroupsLoading}
                        positions={mobile.positionRows}
                        positionColumns={itemColumnDefs}
                        rowConfig={LOANS_ROW_CONFIG}
                        onPositionNavigatePath={(loan) => getLoanAssetPath(loan.loan)}
                        positionsLoading={mobile.positionsLoading}
                        pagination={mobile.pagination}
                        hasHealth
                        hasSelect
                        selection={{
                            selected,
                            setSelected,
                            getKey: (loan) => loan.loan.address,
                            bulkActionNode: mergeAction
                        }}
                        stickyTop={navbarHeight}
                        emptyPlaceholder="No active loans"
                        listLabel={mobile.listLabel}
                        allCard={
                            <PortfolioAllChip
                                loading={!aggregate}
                                count={aggregate?.count}
                                valueUsd={aggregate ? aggregate.principalUsd + aggregate.interestAccruedUsd : undefined}
                                assetCount={mobile.allGroupsTotal}
                                stats={
                                    <HealthApyChipStats
                                        healths={mobile.allHealths}
                                        apy={aggregate?.wAvgApy}
                                        loading={!aggregate}
                                    />
                                }
                            />
                        }
                    />
                ) : (
                    <Table<UserLoanGroup, AbfLoanExpanded>
                        rows={tableRows}
                        columns={groupedColumnDefs}
                        grouping={{
                            columns: itemColumnDefs,
                            getItems: (groupKey) =>
                                typeof groupKey === "string" ? loansByMint?.get(groupKey) : undefined,
                            getDetailKey: (loan) => loan.loan.address,
                            isDetailsLoading: detailsLoading,
                            onDetailRowNavigatePath: (loan) => getLoanAssetPath(loan.loan)
                        }}
                        selection={{ selected, setSelected, expandOnGroupSelect: true }}
                        toolbarActions={mergeAction}
                        paginationProps={{
                            type: "api",
                            pageSize: pagination.pageSize,
                            totalDataCount: totalGroups,
                            controller: getDefaultPaginationController(pagination)
                        }}
                        loading={isLoading}
                        emptyPlaceholder="No active loans"
                        showViewToggle
                        viewToggleStorageKey="PORTFOLIO_LOANS_VIEW"
                    />
                )}
            </TableCardWrapper>
        </SectionCollapse>
    );
}

function useMobileLoansView(isMobile: boolean) {
    const [activeFilter, setActiveFilter] = useState<AllValueType | TableKey>(ALL_VALUE);

    const {
        groups: allGroups,
        totalGroups: allGroupsTotal,
        aggregate,
        isLoading: allGroupsLoading
    } = useUserLoanGroups(
        {
            filterType: LoanFilterType.Active,
            orderFundingTypes: AbfOrderFundingType.Standard,
            sortDirection: SortDirection.Desc,
            page: 0,
            pageSize: MAX_LOANS_PAGE_SIZE
        },
        !isMobile
    );

    const positionsPagination = useLoansPagination({});
    const {
        positions: positionsChunk,
        totalCount: positionsTotal,
        isLoading: positionsLoading
    } = useUserStandardLoansPaginated({
        pagination: positionsPagination.backendPagination,
        principalGroupKey: activeFilter,
        skip: !isMobile
    });
    useClampPaginationOnCountChange(positionsPagination.clampPageToCount, positionsTotal);

    const visiblePositions = useMemo(
        () => positionsPagination.visibleData(positionsChunk),
        [positionsChunk, positionsPagination]
    );

    const chipRows = useMemo(() => createRows<UserLoanGroup>(allGroups ?? [], userLoanGroupTableKey), [allGroups]);
    const positionRows = useMemo(
        () =>
            visiblePositions ? createRows<AbfLoanExpanded>(visiblePositions, (loan) => loan.loan.address) : undefined,
        [visiblePositions]
    );

    const onFilterChange = (next: AllValueType | TableKey) => {
        setActiveFilter(next);
        positionsPagination.resetPagination();
    };

    const activeGroupSymbol = useMemo(() => {
        if (isAll(activeFilter)) return undefined;
        const group = allGroups?.find((g) => userLoanGroupTableKey(g) === activeFilter);
        return group ? BsMetaUtil.getSymbol(group.principalMetadata) : undefined;
    }, [activeFilter, allGroups]);
    const listLabel = `${activeGroupSymbol ?? "All"} loans`;
    const allHealths = useMemo(() => (allGroups ?? []).flatMap((g) => g.rowHealths), [allGroups]);

    const mergeSource = useMemo(
        () => (visiblePositions ? new Map([[EMPTY_STRING, visiblePositions]]) : undefined),
        [visiblePositions]
    );

    const pagination: MobilePortfolioPagination = {
        page: positionsPagination.page,
        setPage: positionsPagination.setPage,
        pageSize: positionsPagination.pageSize,
        totalCount: positionsTotal
    };

    return {
        activeFilter,
        onFilterChange,
        chipRows,
        allGroupsLoading,
        allGroupsTotal,
        aggregate,
        positionRows,
        positionsLoading,
        pagination,
        listLabel,
        allHealths,
        mergeSource
    };
}

function LoanChipContent({ group }: { group: UserLoanGroup | undefined }) {
    return (
        <PortfolioTokenChip
            metadata={group?.principalMetadata}
            count={group?.aggregate.count}
            valueUsd={group ? groupDebtUsd(group) : undefined}
            tokenAmount={group?.borrowedTokenAmount}
            stats={
                <HealthApyChipStats healths={group?.rowHealths ?? []} apy={group?.aggregate.wAvgApy} loading={!group} />
            }
        />
    );
}

function getGroupColumnDefs(): TableColumn<UserLoanGroup>[] {
    return [
        {
            dataIndex: "loan",
            title: "Loan",
            render: (row: TableRow<UserLoanGroup>) => (
                <GroupNameCell metadata={row.data.principalMetadata} count={row.data.aggregate.count} />
            )
        },
        {
            dataIndex: "debt",
            title: "Debt",
            align: "right",
            width: CELL_WIDTH_DEFAULTS.pairValue,
            render: (row: TableRow<UserLoanGroup>) => (
                <PairTokenValueCell
                    usdAmount={row.data.aggregate.principalUsd + row.data.aggregate.interestAccruedUsd}
                    tokenAmount={row.data.borrowedTokenAmount}
                    metadata={row.data.principalMetadata}
                    isRawTokenAmount
                />
            )
        },
        {
            dataIndex: "collateral",
            title: "Collateral",
            align: "right",
            width: CELL_WIDTH.lg,
            render: (row: TableRow<UserLoanGroup>) => (
                <PairTokenValueCell usdAmount={row.data.aggregate.collateralUsd} />
            )
        },
        {
            dataIndex: "health",
            title: "Health",
            align: "right",
            width: CELL_WIDTH_DEFAULTS.health,
            render: (row: TableRow<UserLoanGroup>) => <GroupHealthCell healths={row.data.rowHealths} />
        },
        {
            dataIndex: "borrowApy",
            title: "Borrow APY",
            tooltip: GROUP_HEADER_CELL_TOOLTIPS.loans.apy,
            align: "right",
            width: CELL_WIDTH.lg,
            render: (row: TableRow<UserLoanGroup>) => formatPercent(row.data.aggregate.wAvgApy)
        },
        {
            dataIndex: TABLE_CTA_COLUMN_KEY,
            title: EMPTY_STRING,
            align: "center",
            width: CELL_WIDTH_DEFAULTS.cta,
            render: GROUP_CTA_CELL
        }
    ];
}

export function getLoanDetailColumnDefs(open: AppDialogOpen): TableColumn<AbfLoanExpanded>[] {
    return [
        {
            dataIndex: "loan",
            title: "Loan",
            render: (row) => <LoanName loanExpanded={row.data} variant="body1" condensed fullSymbolOnHover />
        },
        {
            dataIndex: "debt",
            title: "Debt",
            align: "right",
            width: CELL_WIDTH_DEFAULTS.pairValue,
            render: (row) => (
                <PairTokenValueCell
                    tokenAmount={getZcLedger(row.data)?.ledgerDebt.total}
                    usdAmount={row.data.principalUsd}
                    metadata={row.data.principalMetadata}
                />
            ),
            sortBy: (row) => summarizeLedgerAccount(getZcLedger(row.data))?.totalOutstanding ?? 0
        },
        {
            dataIndex: "collateral",
            title: "Collateral",
            align: "right",
            width: CELL_WIDTH.lg,
            render: (row) => <PairTokenValueCell usdAmount={row.data.collateralUsd} />,
            sortBy: (row) => row.data.collateralUsd ?? 0
        },
        {
            dataIndex: "health",
            title: "Health",
            align: "right",
            width: CELL_WIDTH_DEFAULTS.health,
            render: (row) => <LoanHealthTag loan={row.data} variant="body1" textOnly />,
            sortBy: (row) => calculateLoanHealth(row.data).health ?? 0
        },
        {
            dataIndex: "borrowApy",
            title: "Borrow APY",
            align: "right",
            width: CELL_WIDTH.lg,
            render: (row) => <TableCell value={formatPercent(row.data.borrowerWAvgApy)} />,

            sortBy: (row) => row.data.borrowerWAvgApy ?? 0
        },
        {
            dataIndex: TABLE_CTA_COLUMN_KEY,
            title: EMPTY_STRING,
            width: CELL_WIDTH_DEFAULTS.cta,
            align: "center",
            render: (row) => <CtaCell loan={row.data} open={open} />
        }
    ];
}

function CtaCell({ loan, open }: { loan: AbfLoanExpanded; open: AppDialogOpen }) {
    const onActionClick = (action: LoanAction) => {
        if (!loan) return;
        open(AppDialog.LoanActions, {
            loan,
            isDrawer: action.isDrawer,
            action: {
                component: action.component,
                dialogHeaderLabel: action.dialogHeaderLabel
            }
        });
    };
    return (
        <LoanActionsButton
            onActionClick={onActionClick}
            loanExpanded={loan}
            loopPosition={undefined}
            isNestedDialogView={false}
        />
    );
}

export function BulkLoanActionsButton({
    selectedLoanAddresses,
    mergeDisabledReason
}: {
    selectedLoanAddresses: string[];
    mergeDisabledReason?: string;
}) {
    const { open } = useAppDialog();

    return (
        <BulkActionsButton
            count={selectedLoanAddresses.length}
            disabled={selectedLoanAddresses.length < 2}
            actions={[
                {
                    label: "Merge",
                    onClick: () => open(AppDialog.MergeLoans, { loanAddresses: selectedLoanAddresses }),
                    errorTooltip: mergeDisabledReason
                }
            ]}
        />
    );
}
